import { useEffect, useMemo, useState } from 'react'
import { Button } from '../atoms/Button'
import { Input } from '../atoms/Input'

/**
 * Inline ₹ editor — a decimal-rupee text box + Save button that persists
 * an INTEGER-paisa value. Single source for the "type a rupee amount and
 * Save it on the row" behaviour shared by Variant Pricing (baseline
 * price) and Product Pricing (a Buy variant's baseline cost).
 *
 * Owns its own draft: Save enables only when the parsed draft is valid
 * (≥ 0) and differs from the persisted value; Enter saves, Escape resets.
 * Per SOP 02 DRAFT PRESERVATION the draft resets to the server value when
 * that value moves and the operator isn't mid-edit.
 */
/**
 * Single source for the editor's minimum width — every screen using this
 * component (Variant Pricing, Product Pricing) gets the same floor. Change
 * this one value to resize the input everywhere.
 */
const INPUT_MIN_WIDTH = 'min-w-[10rem]'

interface Props {
  /** The persisted value (paisa) the draft reflects and resets to. */
  readonly valuePaisa: number
  /** Persist the new value; return ok or a message to surface inline. */
  readonly onSave: (
    paisa: number
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly ariaLabel: string
  /** Extra input classes (e.g. text-align). The min-width + mono font are
   *  always applied from the single source above. */
  readonly inputClassName?: string
}

export function InlineRupeeEdit({ valuePaisa, onSave, ariaLabel, inputClassName }: Props): JSX.Element {
  const [draft, setDraft] = useState<string>(formatRupees(valuePaisa))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const draftPaisa = useMemo(() => parseRupeesToPaisa(draft), [draft])
  const draftIsValid = draftPaisa !== null && draftPaisa >= 0
  const draftDiffers = draftIsValid && draftPaisa !== valuePaisa

  useEffect(() => {
    if (!draftDiffers) {
      setDraft(formatRupees(valuePaisa))
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuePaisa])

  const handleSave = async (): Promise<void> => {
    if (!draftDiffers || draftPaisa === null) return
    setSaving(true)
    setError(null)
    const result = await onSave(draftPaisa)
    setSaving(false)
    if (!result.ok) setError(result.message)
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          hasError={!draftIsValid || error !== null}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSave()
            if (e.key === 'Escape') setDraft(formatRupees(valuePaisa))
          }}
          inputMode="decimal"
          className={[INPUT_MIN_WIDTH, 'font-mono', inputClassName].filter(Boolean).join(' ')}
          aria-label={ariaLabel}
        />
        <Button size="sm" onClick={() => void handleSave()} disabled={saving || !draftDiffers}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {error !== null ? (
        <span className="text-body-xs font-body text-terra">{error}</span>
      ) : null}
    </div>
  )
}

function formatRupees(paisa: number): string {
  return (paisa / 100).toFixed(2)
}

function parseRupeesToPaisa(input: string): number | null {
  const trimmed = input.trim()
  if (trimmed === '') return null
  if (!/^[0-9]+(\.[0-9]{1,2})?$/.test(trimmed)) return null
  const [whole, frac = '0'] = trimmed.split('.')
  const paddedFrac = frac.padEnd(2, '0')
  const n = Number(whole) * 100 + Number(paddedFrac)
  return Number.isFinite(n) ? n : null
}
